/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AppContext } from '../context/AppContext'
import InvoiceService from '../services/InvoiceService'
import clsx from 'clsx'

const InvoiceList = () => {
    const { currentUser } = useContext(AppContext)
    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [filterStatus, setFilterStatus] = useState('all')
    const [filterPaymentStatus, setFilterPaymentStatus] = useState('all')
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [dateFilter, setDateFilter] = useState({
        startDate: '',
        endDate: ''
    })
    const [orderDateFilter, setOrderDateFilter] = useState({
        startDate: '',
        endDate: ''
    })
    
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        total_pages: 0,
        has_next_page: false,
        has_prev_page: false,
        from: 0,
        to: 0
    })

    useEffect(() => {
        fetchInvoices()
    }, [currentPage, searchTerm, filterStatus, filterPaymentStatus, itemsPerPage, dateFilter, orderDateFilter])

    const fetchInvoices = async () => {
        setLoading(true)
        
        try {
            const params = {
                offset: (currentPage - 1) * itemsPerPage,
                limit: itemsPerPage,
                globalSearch: searchTerm,
                orderBy: 'entity_id',
                orderType: 'desc'
            }

            // Add invoice date filter
            if (dateFilter.startDate && dateFilter.endDate) {
                params.columns = {
                    invoiceDate: [
                        { filterValue: dateFilter.startDate },
                        { filterValue: dateFilter.endDate }
                    ]
                }
            }

            // Add order date filter
            if (orderDateFilter.startDate && orderDateFilter.endDate) {
                params.columns = {
                    ...params.columns,
                    orderDate: [
                        { filterValue: orderDateFilter.startDate },
                        { filterValue: orderDateFilter.endDate }
                    ]
                }
            }

            // Add status filter
            if (filterStatus && filterStatus !== 'all') {
                params.columns = {
                    ...params.columns,
                    status: [{ filterValue: filterStatus, filterType: '=' }]
                }
            }

            const response = await InvoiceService.getInvoicesList(params)
            
            if (response.success) {
                const invoicesWithPaymentStatus = response.data.map(invoice => ({
                    ...invoice,
                    paymentStatus: InvoiceService.calculatePaymentStatus(invoice)
                }))

                setInvoices(invoicesWithPaymentStatus || [])
                
                // Calculate pagination from the response
                const totalCount = response.count || 0
                setPagination({
                    current_page: currentPage,
                    per_page: itemsPerPage,
                    total: totalCount,
                    total_pages: Math.ceil(totalCount / itemsPerPage),
                    has_next_page: currentPage < Math.ceil(totalCount / itemsPerPage),
                    has_prev_page: currentPage > 1,
                    from: (currentPage - 1) * itemsPerPage + 1,
                    to: Math.min(currentPage * itemsPerPage, totalCount)
                })
            } else {
                console.error('Failed to fetch invoices:', response.errorMessage || response.message)
                setInvoices([])
            }
        } catch (error) {
            console.error('Error fetching invoices:', error)
            setInvoices([])
        } finally {
            setLoading(false)
        }
    }

    // Handle search with debounce
    const [searchDebounceTimer, setSearchDebounceTimer] = useState(null)
    
    const handleSearchChange = (value) => {
        setSearchTerm(value)
        
        // Clear previous timer
        if (searchDebounceTimer) {
            clearTimeout(searchDebounceTimer)
        }
        
        // Set new timer for debounced search
        const timer = setTimeout(() => {
            setCurrentPage(1) // Reset to first page when searching
        }, 500)
        
        setSearchDebounceTimer(timer)
    }

    const handleFilterChange = (type, value) => {
        if (type === 'status') {
            setFilterStatus(value)
        } else if (type === 'paymentStatus') {
            setFilterPaymentStatus(value)
        } else if (type === 'perPage') {
            setItemsPerPage(parseInt(value))
        }
        setCurrentPage(1) // Reset to first page when filtering
    }

    const handleDateFilterChange = (type, filterType, value) => {
        if (filterType === 'invoice') {
            setDateFilter(prev => ({
                ...prev,
                [type]: value
            }))
        } else if (filterType === 'order') {
            setOrderDateFilter(prev => ({
                ...prev,
                [type]: value
            }))
        }
        setCurrentPage(1)
    }

    const handlePageChange = (page) => {
        setCurrentPage(page)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const getPaymentStatusBadge = (paymentStatus) => {
        return InvoiceService.getPaymentStatusBadge(paymentStatus)
    }

    const refreshInvoices = () => {
        setCurrentPage(1)
        setSearchTerm('')
        setFilterStatus('all')
        setFilterPaymentStatus('all')
        setDateFilter({ startDate: '', endDate: '' })
        setOrderDateFilter({ startDate: '', endDate: '' })
        fetchInvoices()
    }

    const handlePrintInvoice = async (invoiceId) => {
        try {
            await InvoiceService.printInvoice(invoiceId)
        } catch (error) {
            console.error('Error printing invoice:', error)
            alert('Error printing invoice. Please try again.')
        }
    }

    const renderPagination = () => {
        if (pagination.total_pages <= 1) return null

        const pages = []
        const maxVisiblePages = 5
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
        const endPage = Math.min(pagination.total_pages, startPage + maxVisiblePages - 1)

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    className={clsx('btn btn-sm mx-1', {
                        'btn-primary': i === currentPage,
                        'btn-light': i !== currentPage
                    })}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            )
        }

        return (
            <div className="d-flex justify-content-center align-items-center mt-4">
                <button
                    className="btn btn-sm btn-light mx-1"
                    disabled={!pagination.has_prev_page}
                    onClick={() => handlePageChange(currentPage - 1)}
                >
                    Previous
                </button>
                {pages}
                <button
                    className="btn btn-sm btn-light mx-1"
                    disabled={!pagination.has_next_page}
                    onClick={() => handlePageChange(currentPage + 1)}
                >
                    Next
                </button>
            </div>
        )
    }

    // Filter invoices by payment status (client-side filter)
    const filteredInvoices = filterPaymentStatus === 'all' 
        ? invoices 
        : invoices.filter(invoice => invoice.paymentStatus === filterPaymentStatus)

    return (
        <div className="container-fluid">
            {/* Page Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="h3 mb-0">Invoice Management</h1>
                    <p className="text-muted">Manage invoices and track payments</p>
                </div>
                <Link to="/sales-invoices/create" className="btn btn-primary">
                    <i className="fas fa-plus me-2"></i>Create New Invoice
                </Link>
            </div>

            {/* Filters */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row g-3">
                        {/* Search */}
                        <div className="col-md-3">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by invoice no, customer name..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>

                        {/* Status Filter */}
                        <div className="col-md-2">
                            <label className="form-label">Order Status</label>
                            <select
                                className="form-select"
                                value={filterStatus}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="pending">Pending</option>
                                <option value="confirm">Confirmed</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="canceled">Canceled</option>
                            </select>
                        </div>

                        {/* Payment Status Filter */}
                        <div className="col-md-2">
                            <label className="form-label">Payment Status</label>
                            <select
                                className="form-select"
                                value={filterPaymentStatus}
                                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
                            >
                                <option value="all">All</option>
                                <option value="unpaid">Unpaid</option>
                                <option value="partially_paid">Partially Paid</option>
                                <option value="paid">Paid</option>
                            </select>
                        </div>

                        {/* Invoice Date Range */}
                        <div className="col-md-2">
                            <label className="form-label">Invoice Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={dateFilter.startDate}
                                onChange={(e) => handleDateFilterChange('startDate', 'invoice', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Invoice End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={dateFilter.endDate}
                                onChange={(e) => handleDateFilterChange('endDate', 'invoice', e.target.value)}
                            />
                        </div>

                        {/* Items per page */}
                        <div className="col-md-1">
                            <label className="form-label">Per Page</label>
                            <select
                                className="form-select"
                                value={itemsPerPage}
                                onChange={(e) => handleFilterChange('perPage', e.target.value)}
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </div>
                    </div>

                    {/* Order Date Range - Second Row */}
                    <div className="row g-3 mt-2">
                        <div className="col-md-2">
                            <label className="form-label">Order Start Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={orderDateFilter.startDate}
                                onChange={(e) => handleDateFilterChange('startDate', 'order', e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Order End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={orderDateFilter.endDate}
                                onChange={(e) => handleDateFilterChange('endDate', 'order', e.target.value)}
                            />
                        </div>

                        {/* Refresh Button */}
                        <div className="col-md-1 d-flex align-items-end">
                            <button
                                className="btn btn-outline-secondary"
                                onClick={refreshInvoices}
                                title="Refresh"
                            >
                                <i className="fas fa-sync-alt"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Invoices Table */}
            <div className="card">
                <div className="card-body">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-2">Loading invoices...</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Invoice ID</th>
                                            <th>Invoice No</th>
                                            <th>Order No</th>
                                            <th>Customer</th>
                                            <th>Vendor</th>
                                            <th>Invoice Date</th>
                                            <th>Order Date</th>
                                            <th>Amount</th>
                                            <th>Order Status</th>
                                            <th>Payment Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInvoices.length > 0 ? (
                                            filteredInvoices.map((invoice) => (
                                                <tr key={invoice.invoiceId}>
                                                    <td>#{invoice.invoiceId}</td>
                                                    <td>
                                                        <Link 
                                                            to={`/sales-invoices/${invoice.invoiceId}`}
                                                            className="text-decoration-none fw-bold"
                                                        >
                                                            {invoice.invoiceNo}
                                                        </Link>
                                                    </td>
                                                    <td>
                                                        <Link 
                                                            to={`/sales-orders/${invoice.orderId || '#'}`}
                                                            className="text-decoration-none"
                                                        >
                                                            {invoice.orderNo}
                                                        </Link>
                                                    </td>
                                                    <td>{invoice.customerName || 'N/A'}</td>
                                                    <td>{invoice.vendorName || 'N/A'}</td>
                                                    <td>{InvoiceService.formatDateShort(invoice.invoiceDate)}</td>
                                                    <td>{InvoiceService.formatDateShort(invoice.orderDate)}</td>
                                                    <td>{InvoiceService.formatCurrency(invoice.amount)}</td>
                                                    <td>
                                                        <span className={clsx('badge', `badge-${invoice.status === 'delivered' ? 'success' : 'warning'}`)}>
                                                            {invoice.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={clsx('badge', getPaymentStatusBadge(invoice.paymentStatus))}>
                                                            {invoice.paymentStatus?.replace('_', ' ')}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="btn-group btn-group-sm">
                                                            <Link
                                                                to={`/sales-invoices/${invoice.invoiceId}`}
                                                                className="btn btn-outline-primary btn-sm"
                                                                title="View Details"
                                                            >
                                                                <i className="fas fa-eye"></i>
                                                            </Link>
                                                            <button
                                                                className="btn btn-outline-info btn-sm"
                                                                onClick={() => handlePrintInvoice(invoice.invoiceId)}
                                                                title="Print Invoice"
                                                            >
                                                                <i className="fas fa-print"></i>
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="11" className="text-center py-4">
                                                    <div className="text-muted">
                                                        <i className="fas fa-file-invoice fa-3x mb-3"></i>
                                                        <p>No invoices found</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Info */}
                            {filteredInvoices.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {pagination.from} to {pagination.to} of {pagination.total} entries
                                    </div>
                                    {renderPagination()}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default InvoiceList 